/**
 * AuthProvider — Clerk wrapper.
 *
 * S-055 / FEAT-006. The `<ClerkProvider>` shell plus a canonical
 * `useCurrentUser()` hook exposing the Clerk-shaped fields the SPA
 * actually consumes.
 *
 * S-073 (Phase 9) removed the legacy `useAuth()` compat shim that
 * adapted the Clerk shape to the Directus snake_case fields
 * (`first_name`, `last_name`, `role`) for the old TopBar / LoginView
 * call sites. TopBar now reads `useCurrentUser()` directly and calls
 * Clerk's `useClerk().signOut()` when the user signs out.
 *
 * The Clerk publishable key is read from `VITE_CLERK_PUBLISHABLE_KEY`
 * at build time. Missing the key is a startup error — there is no
 * fallback, the SPA cannot authenticate without it.
 */

import { ClerkProvider, useUser } from "@clerk/clerk-react";

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

if (!publishableKey) {
  // Surface this at startup rather than inside a render so the failure
  // is unambiguous in the browser console.
  // eslint-disable-next-line no-console
  console.error(
    "[AuthProvider] VITE_CLERK_PUBLISHABLE_KEY is not set. " +
      "Add it to apps/web/.env (see .env.example)."
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // ClerkProvider must always render — if the key is missing it throws
  // a clear runtime error, which is the desired behavior for misconfig.
  return (
    // BUG-077 — without these, Clerk does not know this app has its own auth
    // pages. <RedirectToSignIn/> in RequireAuth fell back to the hosted Account
    // Portal at hot-raccoon-76.accounts.dev, so an unauthenticated visitor —
    // and anyone asking for access — landed on a cryptic unbranded URL.
    <ClerkProvider
      publishableKey={publishableKey ?? ""}
      signInUrl="/login"
      signUpUrl="/join"
      waitlistUrl="/join"
      // The card's own heading: no "Sign in to {app}", which repeated the
      // brand the page already shows beside it.
      localization={{ signIn: { start: { title: "Sign in", subtitle: "Welcome back." } } }}
    >
      {children}
    </ClerkProvider>
  );
}

/** Canonical Clerk-shaped current-user hook. */
export interface CurrentUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

export function useCurrentUser(): { user: CurrentUser | null; loading: boolean } {
  const { isLoaded, user } = useUser();
  if (!isLoaded) return { user: null, loading: true };
  if (!user) return { user: null, loading: false };
  return {
    user: {
      id: user.id,
      email: user.primaryEmailAddress?.emailAddress ?? "",
      firstName: user.firstName,
      lastName: user.lastName,
    },
    loading: false,
  };
}

/**
 * FEAT-031 — platform-operator flag, read from Clerk publicMetadata (the
 * source of the `is_platform_admin` JWT claim the API enforces). Client-side
 * gate for the Admin Analytics route + nav link; the API is the real guard.
 */
export function usePlatformAdmin(): boolean {
  const { isLoaded, user } = useUser();
  if (!isLoaded || !user) return false;
  return (user.publicMetadata as Record<string, unknown>)?.is_platform_admin === true;
}

/** "LC" from "Luis Capobianco" or "lu" from "luis@..." as a fallback. */
export function userInitials(u: CurrentUser | null | undefined): string {
  if (!u) return "?";
  const parts = [u.firstName, u.lastName].filter(Boolean) as string[];
  if (parts.length) return parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return u.email.slice(0, 2).toUpperCase();
}

/** Full name when available, otherwise the email. */
export function userDisplayName(u: CurrentUser | null | undefined): string {
  if (!u) return "";
  const parts = [u.firstName, u.lastName].filter(Boolean) as string[];
  return parts.length ? parts.join(" ") : u.email;
}
